import { useClinicDocumentTitle } from "@clinic/clinicQueries";
import { AccessCodeScreen } from "@components/AccessCodeScreen";
import { AppShell } from "@components/AppShell";
import { Crossfade } from "@components/Crossfade";
import { LoginScreen } from "@components/LoginScreen";
import { OfflineNotice } from "@components/OfflineNotice";
import { useAuth } from "@context/AuthContext";
import { useUser } from "@context/UserContext";
import { DisplayHome } from "@displays/DisplayHome";
import { DisplayPairingScreen } from "@displays/DisplayPairingScreen";
import { isDisplayPath, showDisplayPath } from "@displays/displayPath";
import { useDisplayScreen } from "@displays/displaysQueries";
import { AppProviders } from "@providers/AppProviders";
import { type StaleReload, StaleReloader } from "@version/StaleReloader";
import { useEffect } from "react";

type Stage = "checking" | "display" | "pairing" | "login" | "select" | "app";

// Signed-in staff get `NewBuildNotice` instead; a reload could cut off a recording or edit.
const STALE_RELOAD: Partial<Record<Stage, StaleReload>> = {
  display: "now",
  pairing: "now",
  login: "when-idle",
  select: "when-idle",
};

const renderStage = (stage: Stage) => {
  if (stage === "checking") return null;
  if (stage === "display") return <DisplayHome />;
  if (stage === "pairing") return <DisplayPairingScreen />;
  if (stage === "login") return <LoginScreen />;
  if (stage === "select") return <AccessCodeScreen />;
  return <AppShell />;
};

const GatedApp = () => {
  useClinicDocumentTitle();
  const { isAuthed } = useAuth();
  const { isUserAuthed } = useUser();
  // A staff session wins, so pairing a workstation by mistake never hides it.
  const screen = useDisplayScreen(!isAuthed);
  const isDisplay = !isAuthed && !!screen.data;

  // Keeps an unpaired or reloaded screen on the pairing page rather than sign-in.
  useEffect(() => {
    if (isDisplay) showDisplayPath();
  }, [isDisplay]);

  const stage: Stage = isAuthed
    ? isUserAuthed
      ? "app"
      : "select"
    : isDisplay
      ? "display"
      : screen.isPending && screen.failureCount === 0
        ? "checking"
        : isDisplayPath()
          ? "pairing"
          : "login";

  const staleReload = STALE_RELOAD[stage];

  return (
    <>
      <Crossfade value={stage} render={renderStage} />
      {staleReload && <StaleReloader mode={staleReload} />}
    </>
  );
};

export const App = () => (
  <AppProviders>
    <GatedApp />
    {/* Outside the gate so the login screen shows it too. */}
    <OfflineNotice />
  </AppProviders>
);
